import React, { useState } from "react";
import { FileText, AlertCircle } from "lucide-react";

const TextInputStep = ({ textInput, setTextInput, showToast }) => {
  const MIN_LENGTH = 100;
  const MAX_LENGTH = 50000;

  const handleTextChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_LENGTH) {
      setTextInput(value);
    }
  };

  const characterCount = textInput.length;
  const isValid = characterCount >= MIN_LENGTH && characterCount <= MAX_LENGTH;

  return (
    <div className="space-y-5 transition-colors duration-200">
      {/* Text Area */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
          Enter or paste your requirements, tasks, or documentation
        </label>
        <textarea
          value={textInput}
          onChange={handleTextChange}
          placeholder="Enter text containing actionable tasks or requirements. AI will extract tickets with titles, descriptions, priorities, and statuses..."
          className={`w-full h-64 px-4 py-3 rounded-lg border-2 transition-all resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-black dark:text-white ${
            characterCount === 0
              ? "border-gray-300 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50"
              : isValid
              ? "border-green-400 dark:border-green-500 bg-green-50/30 dark:bg-green-900/10"
              : characterCount < MIN_LENGTH
              ? "border-yellow-400 dark:border-yellow-500 bg-yellow-50/30 dark:bg-yellow-900/10"
              : "border-red-400 dark:border-red-500 bg-red-50/30 dark:bg-red-900/10"
          }`}
        />

        {/* Character Count */}
        <div className="flex justify-between items-center text-xs">
          <span
            className={`font-medium ${
              characterCount === 0
                ? "text-gray-500 dark:text-slate-500"
                : isValid
                ? "text-green-600 dark:text-green-400"
                : characterCount < MIN_LENGTH
                ? "text-yellow-600 dark:text-yellow-400"
                : "text-red-600 dark:text-red-400"
            }`}
          >
            {characterCount >= MIN_LENGTH
              ? `${characterCount.toLocaleString()} characters`
              : `${MIN_LENGTH - characterCount} more characters needed`}
          </span>
          <span className="text-gray-500 dark:text-slate-500">
            {characterCount.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Validation Message */}
      {characterCount > 0 && !isValid && (
        <div
          className={`flex gap-3 p-3 rounded-lg border transition-colors duration-200 ${
            characterCount < MIN_LENGTH
              ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800"
              : "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
          }`}
        >
          <AlertCircle
            className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              characterCount < MIN_LENGTH ? "text-yellow-600 dark:text-yellow-400" : "text-red-600 dark:text-red-400"
            }`}
          />
          <div className="text-sm">
            {characterCount < MIN_LENGTH ? (
              <>
                <p className="font-medium text-yellow-900 dark:text-yellow-200">More text needed</p>
                <p className="text-yellow-700 dark:text-yellow-300/80">
                  Please provide at least {MIN_LENGTH} characters for meaningful ticket extraction
                </p>
              </>
            ) : (
              <>
                <p className="font-medium text-red-900 dark:text-red-200">Text limit exceeded</p>
                <p className="text-red-700 dark:text-red-300/80">
                  Maximum length is {MAX_LENGTH.toLocaleString()} characters
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Info */}
      <div className="flex gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg transition-colors duration-200">
        <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900 dark:text-blue-200">
          <p className="font-medium mb-1">AI will extract:</p>
          <p className="text-blue-700 dark:text-blue-300/80">
            Titles, descriptions, priorities, and statuses from your text content
          </p>
        </div>
      </div>
    </div>
  );
};

export default TextInputStep;
